/**
 * ThemeSwitcher Component
 * Dropdown to switch between 4 themes: Light, Dark, Cyberpunk, Luxury
 */

import React, { useState, useRef, useEffect } from 'react';
import { Palette, SunMedium, MoonStar, Zap, Gem, Check } from 'lucide-react';
import { useTheme, THEMES } from '../contexts/ThemeContext';

const ThemeSwitcher = () => {
    const { theme, setTheme } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getThemeIcon = (themeId) => {
        switch (themeId) {
            case 'light': return <SunMedium size={18} className="text-amber-500" />;
            case 'dark': return <MoonStar size={18} className="text-indigo-400" />;
            case 'cyberpunk': return <Zap size={18} className="text-cyan-400" />;
            case 'luxury': return <Gem size={18} className="text-amber-600" />;
            default: return <Palette size={18} />;
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                title="Ganti Tema"
            >
                {getThemeIcon(theme)}
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-2 z-50 animate-in fade-in slide-in-from-top-2">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            THEME
                        </span>
                    </div>

                    {Object.values(THEMES).map((themeOption) => (
                        <button
                            key={themeOption.id}
                            onClick={() => {
                                setTheme(themeOption.id);
                                setIsOpen(false);
                            }}
                            className={`w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${theme === themeOption.id ? 'bg-purple-50 dark:bg-purple-900/30' : ''
                                }`}
                        >
                            <span className="text-lg">{getThemeIcon(themeOption.id)}</span>
                            <span className={`flex-1 text-left font-medium ${theme === themeOption.id
                                ? 'text-purple-600 dark:text-purple-400'
                                : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                {themeOption.name}
                            </span>
                            {theme === themeOption.id && (
                                <Check size={16} className="text-purple-600 dark:text-purple-400" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ThemeSwitcher;
